import { LoadingLogo } from '@/components/common/LoadingLogo';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Protected route component that restricts access to admin users only.
 * Redirects to login if user is not authenticated, and to dashboard if user is not an admin.
 *
 * **Note:** This component should be wrapped inside a ProtectedRoute to ensure
 * authentication is checked first.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if user is authorized
 * @returns {React.ReactElement} The protected content or a redirect
 *
 * @example
 * <ProtectedRoute>
 *   <AdminRoute>
 *     <AdminDashboard />
 *   </AdminRoute>
 * </ProtectedRoute>
 */
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading state while authentication is being checked
  if (loading) {
    return <LoadingLogo />;
  }

  // Redirect to login if not authenticated (shouldn't happen if wrapped in ProtectedRoute)
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to dashboard if user is not an admin
  if (user.role !== 'admin') {
    return (
      <Navigate
        to="/dashboard"
        replace
        state={{
          message: 'You do not have permission to access the admin area.',
          type: 'warning',
        }}
      />
    );
  }

  // Render protected content
  return children;
};

AdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AdminRoute;
